// Standard library imports
import React, { useEffect, useState } from 'react';

// External library imports
import { Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import { UilImport } from '@iconscout/react-unicons';
import { Menu, MenuItem } from '@szhsin/react-menu';

// Internal module imports
import { Input } from '../../../../components/Inputs/Input';
import Dropdown from '../../../../components/Inputs/Dropdown';
import { useLoader, useQueryParamWithDefault } from '../../../../hooks';
import MonthPicker from '../../../../components/Inputs/MonthPicker/MonthPicker';
import { momentTimeFormater } from '../../../../utils/dateHelper';
import Typography from '../../../../components/Typography/Typography';
import { RootRevenueCalculatorService } from '../../../../services/RootRevenueCalculatorService';
import { toDkFormatter } from '../../../../utils/currencyHelper';
import { DownloadAsExcel } from '../../../../utils/downloadAsExcel';
import Table from '../../../../components/Table/Table';
import { isValidTimeRange } from '../../../../utils/timeHelper';
import EChart from '../../../../components/Echarts/Echarts';
import { DateParam, NumberParam, StringParam } from 'use-query-params';

// Css imports
import RevenueClasses from './index.module.css';
import moment from 'moment';

const LIMIT = 15;
const MARKET_OPTIONS = [
    {
        label: 'FCR',
        value: 'FCR',
    },
    {
        label: 'FCR-D-INC',
        value: 'FCR-D_INCREASED',
    },
    {
        label: 'FCR-D-DEC',
        value: 'FCR-D_DECREASED',
    },
    {
        label: 'FCR-D (LER)',
        value: 'FCR-D (LER)',
    },
    {
        label: 'FCR-N',
        value: 'FCR-N',
    },
];

const AFRR_MARKET_OPTIONS = [
    {
        label: 'AFRR-INC',
        value: 'AFRR_INCREASED',
    },
    {
        label: 'AFRR-DEC',
        value: 'AFRR_DECREASED',
    },
];

const MFRR_MARKET_OPTIONS = [
    {
        label: 'MFRR-INC',
        value: 'MFRR_INCREASED',
    },
    {
        label: 'MFRR-DEC',
        value: 'MFRR_DECREASED',
    },
];

const CURRENCY_OPTIONS = [
    {
        label: 'DKK',
        value: 'DKK',
    },
    {
        label: 'Euro',
        value: 'Euro',
    },
];

const AREA_OPTIONS = [
    {
        label: 'DK1',
        value: 'DK1',
    },
    {
        label: 'DK2',
        value: 'DK2',
    },
    {
        label: 'SE1',
        value: 'SE1',
    },
    {
        label: 'SE2',
        value: 'SE2',
    },
    {
        label: 'SE3',
        value: 'SE3',
    },
    {
        label: 'SE4',
        value: 'SE4',
    },
];

const today = new Date();
const sixMonthsAgo = new Date();
sixMonthsAgo.setMonth(today.getMonth() - 6);

const RevenueCalulator = ({ cId }) => {
    const [startLoader, stopLoader] = useLoader();
    const [startDate, setStartDate] = useQueryParamWithDefault('startDate', DateParam, moment().subtract(6, 'months').toDate(), cId);
    const [endDate, setEndDate] = useQueryParamWithDefault('endDate', DateParam, moment().toDate(), cId);
    const [data, setData] = useState({});
    const [graphData, setGraphData] = useState([]);
    const [chartData, setChartData] = useState({});
    const [downloadData, setDownloadData] = useState([]);
    const [detailedRevenue, setDetailedRevenue] = useState([]);
    const [selectedCurrency, setSelectedCurrency] = useQueryParamWithDefault('currency', StringParam, CURRENCY_OPTIONS[0].value, cId);
    const [areaOption, setAreaOption] = useState(AREA_OPTIONS);
    const [selectedArea, setSelectedArea] = useQueryParamWithDefault('area', StringParam, areaOption[0].value, cId);
    const [capacity, setCapacity] = useQueryParamWithDefault('capacity', NumberParam, 1, cId);
    const [marketOption, setMarketOption] = useState(selectedArea === 'DK1' ? [MARKET_OPTIONS[0], ...AFRR_MARKET_OPTIONS, ...MFRR_MARKET_OPTIONS] : MARKET_OPTIONS);
    const [selectedMarket, setSelectedMarket] = useQueryParamWithDefault('market', StringParam, MARKET_OPTIONS[0].value, cId);
    const [skip, setSkip] = useState(0);
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    const initialValues = {
        market: '',
        area: '',
        capacity: capacity || 1,
    };

    useEffect(() => {
        if (selectedMarket && startDate && endDate && selectedArea) {
            getRevenue({
                signal: selectedMarket,
                capacity: capacity,
                startDate: momentTimeFormater(startDate).format('YYYY-MM-DD'),
                endDate: momentTimeFormater(endDate).format('YYYY-MM-DD'),
                area: selectedArea,
            });
        }
    }, [selectedMarket]);

    const handleSubmit = (values) => {
        if (!isValidTimeRange(startDate, endDate)) {
            toast.error('Invalid month');
            return;
        }
        getRevenue({
            signal: values.market,
            capacity: values.capacity,
            startDate: momentTimeFormater(startDate).format('YYYY-MM-DD'),
            endDate: momentTimeFormater(endDate).format('YYYY-MM-DD'),
            area: selectedArea,
        });
        setCapacity(values.capacity);
    };

    const processDetailedRevenue = (data) => {
        return data.map((item) => ({
            date: item.date,
            dkk: item.revenueDkk,
            euro: item.revenueEuro,
        }));
    };

    const getRevenue = (params) => {
        RootRevenueCalculatorService.getRevenue(params, startLoader, handleSuccess, handleError, stopLoader);
    };

    const handleSuccess = ({ data }) => {
        setData(data.data);
        setGraphData(data.data.graphData || []);
        setDownloadData(data.data.downloadData || []);
        const dailyRevenue = processDetailedRevenue(data.data.detailedRevenue);
        setDetailedRevenue(dailyRevenue || []);
        setTotalCount(dailyRevenue?.length || 0);
        if (selectedCurrency === 'DKK') {
            setChartData(data.data.graphData[0] || {});
        } else {
            setChartData(data.data.graphData[1] || {});
        }
    };

    const handleError = (error) => {
        let message = error?.response?.data?.message;
        toast.error(message);
    };

    const handleDownLoad = () => {
        DownloadAsExcel(downloadData, `Potential Revenue - Months - ${momentTimeFormater(startDate).format('YYYY-MM-DD')} - ${momentTimeFormater(endDate).format('YYYY-MM-DD')}`, [
            'Month',
            'Revenue DKK',
            'Revenue Euro',
        ]);
    };

    const handleDownLoadDayData = () => {
        DownloadAsExcel(detailedRevenue, `Potential Revenue - Days -${momentTimeFormater(startDate).format('YYYY-MM-DD')} - ${momentTimeFormater(endDate).format('YYYY-MM-DD')}`, [
            'Date',
            'Revenue DKK',
            'Revenue Euro',
        ]);
    };

    const handlePageChange = (pageno) => {
        setSkip(LIMIT * pageno);
        setPage(pageno);
    };

    return (
        <div>
            <div className={RevenueClasses.Events + ` ${RevenueClasses.FilterWarapper}`}>
                <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
                    {({ errors, touched, values, isValidating, ...props }) => (
                        <Form style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div className={RevenueClasses.InputContainer}>
                                    <div>
                                        <div className={RevenueClasses.FieldControl} style={{ width: 'auto' }}>
                                            <label for="area">Select Area</label>
                                            <Dropdown
                                                name="area"
                                                label="area"
                                                options={areaOption}
                                                defaultValue={{ value: selectedArea }}
                                                onChange={(value) => {
                                                    if (value.value === 'DK1') {
                                                        setMarketOption([MARKET_OPTIONS[0], ...AFRR_MARKET_OPTIONS, ...MFRR_MARKET_OPTIONS]);
                                                        setSelectedMarket(MARKET_OPTIONS[0].value);
                                                    } else if (value.value === 'DK2') {
                                                        let temp = MARKET_OPTIONS.filter((item) => item.value !== 'FCR');
                                                        temp.push(...AFRR_MARKET_OPTIONS, ...MFRR_MARKET_OPTIONS);
                                                        setMarketOption(temp);
                                                        setSelectedMarket(temp[0].value);
                                                    } else {
                                                        let temp = MARKET_OPTIONS.filter((item) => item.value !== 'FCR' && item.value !== 'FCR-N');
                                                        setMarketOption(temp);
                                                        setSelectedMarket(temp[0].value);
                                                    }
                                                    setSelectedArea(value.value);
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <div className={RevenueClasses.FieldControl} style={{ width: 'auto', minWidth: '10vw' }}>
                                            <label for="market">Select Market</label>
                                            <Dropdown name="market" label="market" options={marketOption} defaultValue={{ value: selectedMarket }} />
                                        </div>
                                    </div>

                                    <div>
                                        <div className={RevenueClasses.FieldControl}>
                                            <label for="capacity">Capacity (MW)</label>
                                            <Input name="capacity" />
                                        </div>
                                    </div>
                                    <div className={RevenueClasses.FieldControl} style={{ width: 'auto' }}>
                                        <label for="currency">Select Currency</label>
                                        <Dropdown
                                            name="currency"
                                            label="Currency"
                                            options={CURRENCY_OPTIONS}
                                            defaultValue={{ value: selectedCurrency }}
                                            onChange={(value) => {
                                                if (value.value === 'DKK') setChartData(graphData[0] || {});
                                                else setChartData(graphData[1] || {});

                                                setSelectedCurrency(value.value);
                                            }}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5vw' }}>
                                        <div className={RevenueClasses.DateLabels} style={{ marginBottom: '0' }}>
                                            Start Month
                                            <MonthPicker date={startDate} setDate={(e) => setStartDate(e)} />
                                        </div>
                                        <div className={RevenueClasses.DateLabels} style={{ marginBottom: '0' }}>
                                            End Month
                                            <MonthPicker date={endDate} setDate={(e) => setEndDate(e)} />
                                        </div>
                                        <div className={RevenueClasses.SubmitButton}>
                                            <button className="btn-primary">Submit</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>

                <div className={RevenueClasses.Icon}>
                    <Menu
                        menuButton={
                            <div>
                                <UilImport
                                    style={{
                                        cursor: 'pointer',
                                        color: 'var(--color-primary)',
                                    }}
                                />
                            </div>
                        }
                        direction={'bottom'}
                        arrow={true}
                        menuClassName={RevenueClasses.Menu}
                    >
                        <MenuItem onClick={() => handleDownLoad()} className={RevenueClasses.MenuOptions}>
                            <div>Months</div>
                        </MenuItem>
                        <MenuItem onClick={() => handleDownLoadDayData()} className={RevenueClasses.MenuOptions}>
                            <div>Days</div>
                        </MenuItem>
                    </Menu>
                </div>
            </div>
            <div className={RevenueClasses.ChartContainer}>
                <div>
                    <div className={RevenueClasses.CardContainer}>
                        <div className={RevenueClasses.Card}>
                            Revenue In DKK :
                            <span>
                                <Typography size="14" content={(data?.totalRevenueInDkk && toDkFormatter(data?.totalRevenueInDkk)) || 0} />
                            </span>
                        </div>
                        <div className={RevenueClasses.Card}>
                            Revenue In Euro :
                            <span>
                                <Typography size="14" content={(data?.totalRevenueInEuro && toDkFormatter(data?.totalRevenueInEuro)) || 0} />
                            </span>
                        </div>
                    </div>
                </div>
                <div style={{ marginTop: '1vw', height: '40vh' }}>
                    <EChart
                        title={`Revenue in ${selectedCurrency}`}
                        xAxisProps={{ type: 'category' }}
                        type="bar"
                        series={[chartData]}
                        yFormatter={toDkFormatter}
                        yTooltipSuffix={selectedCurrency === 'DKK' ? ['kr'] : ['€']}
                    />
                </div>
                <div style={{ marginTop: '1vw', padding: '1vw' }}>
                    <Typography content={'Revenue Summary'} />

                    <Table
                        head={['Date', 'Revenue DKK', 'Revenue Euro']}
                        keys={['date', 'dkk', 'euro']}
                        data={detailedRevenue.slice(LIMIT * page, LIMIT * page + LIMIT)}
                        page={page}
                        Pagination={true}
                        limit={LIMIT}
                        handlePageChange={handlePageChange}
                        totalCount={totalCount}
                    />
                </div>
            </div>
        </div>
    );
};

export default RevenueCalulator;
